

import { useState, useContext, createContext } from "react";

import { AdminOverlay } from "../components/AdminPanel";

import { PromptTemplateType, usePromptUpdate } from "./PromptContext";
import { APP, ENVIRONMENT, AI4YOUTH, ENRICHYA, QA, PROD, DEV } from "../common/constants";



interface AdminSection {
  id: string;
  label: string;
  subsections: Record<string, string>;
}


interface AdminContextType {
    isAdminMode: boolean;

    selectedSection: string;
    selectedSubsection: string;
    sections: Record<string, AdminSection>;

    selectedPlatform: string;
    selectedEnvironment: string;
}

interface AdminUpdateContextType {
    toggleAdminMode: () => void;

    changeSelectedSection: (section: string) => void;
    changeSelectedSubsection: (subsection: string) => void;

    changeSelectedPlatform: (platform: string) => void;
    changeSelectedEnvironment: (environment: string) => void;
}


const AdminContext = createContext<AdminContextType | null>(null);
const AdminUpdateContext = createContext<AdminUpdateContextType | null>(null);


function useAdmin() {
    const context = useContext(AdminContext);
    if (!context) throw new Error("useAdmin must be used within AdminContextProvider");
    return context;
}

function useAdminUpdate() {
    const context = useContext(AdminUpdateContext);
    if (!context) throw new Error("useAdminUpdate must be used within AdminContextProvider");
    return context;
}



interface ComponentProps {
    children: React.ReactNode;
}

function AdminContextProvider({ children }: ComponentProps) {
    const [isAdminMode, setIsAdminMode] = useState<boolean>( false );

    const { getPromptCategories } = usePromptUpdate();


    const [selectedSection, setSelectedSection] = useState<string>("prompts");
    const [selectedSubsection, setSelectedSubsection] = useState<string>("context");

    // Platform and environment selection state - default to current app's platform/environment
    const [selectedPlatform, setSelectedPlatform] = useState<string>(APP);
    const [selectedEnvironment, setSelectedEnvironment] = useState<string>(ENVIRONMENT);

    const sections: Record<string, AdminSection> = {
        prompts: {
            id: "prompts",
            label: "Prompts",
            subsections: getPromptCategories().reduce((acc, category) => {
                acc[category] = category;
                return acc;
            }, {} as Record<string, string>) || {}
        },

        promptsGenerator: {
            id: "promptsGenerator",
            label: "Prompts Generator",
            subsections: getPromptCategories(PromptTemplateType.GENERATED).reduce((acc, category) => {
                acc[category] = category;
                return acc;
            }, {} as Record<string, string>) || {}
        }
    };

    const changeSelectedSection = (section: string) => {
        setSelectedSection(section);
    };

    const changeSelectedSubsection = (subsection: string) => {
        setSelectedSubsection(subsection);
    };

    const changeSelectedPlatform = (platform: string) => {
        setSelectedPlatform(platform);
    };

    const changeSelectedEnvironment = (environment: string) => {
        setSelectedEnvironment(environment);
    };


    const toggleAdminMode = () => {
        setIsAdminMode(previous => !previous);
    };


    return (
        <AdminContext.Provider value={{
            isAdminMode,
            selectedSection,
            selectedSubsection,
            sections,
            selectedPlatform,
            selectedEnvironment
        }}>
            <AdminUpdateContext.Provider value={{
                toggleAdminMode,
                changeSelectedSection,
                changeSelectedSubsection,
                changeSelectedPlatform,
                changeSelectedEnvironment
            }}>
                {children}
                {isAdminMode && <AdminOverlay />}
            </AdminUpdateContext.Provider>
        </AdminContext.Provider>
    );
}





export {
    AdminContextProvider,
    useAdmin, useAdminUpdate
};

